import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { teknikTokens, teknikButtons } from '../styles/teknik-tokens.js';
import { tkPrimitives } from '../styles/tk-primitives.js';
import { tkAuthCard } from '../styles/tk-auth.js';

/**
 * The 2FA challenge.
 *
 * There used to be a "use a backup code instead" toggle. It was misleading in
 * one direction and load-bearing in another:
 *
 *  - Misleading: both branches POST an identical body to an identical endpoint.
 *    `/auth/teknik/2fa/verify` destructures only `token` and self-detects —
 *    it tries TOTP first, then falls back to the stored backup codes. So the
 *    "choice" never reached the server.
 *  - Load-bearing: it widened `maxlength` from 6 to 10, which a backup code
 *    needs, since they are minted as 10 hex characters.
 *
 * And `inputmode="numeric"` was hardcoded across both modes, so a phone offered
 * a numeric keypad for a code that contains a–f — backup codes were effectively
 * untypeable on mobile.
 *
 * So: one input, sized and typed for either credential, and copy that says so.
 */
@customElement('app-two-factor')
export class AppTwoFactor extends LitElement {
  @state() code = '';
  @state() loading = false;
  @state() error = '';
  @state() notice = '';

  /** Same-origin only; see the matching guard in app-login.ts. */
  private returnPath(): string {
    const raw = new URLSearchParams(window.location.search).get('return') || '';
    if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
    return raw;
  }

  async handleVerify(e: Event) {
    e.preventDefault();
    this.error = '';
    this.loading = true;
    try {
      const res = await fetch('/auth/teknik/2fa/verify', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // The server compares backup codes with an exact `includes()`, and they
        // are stored lowercase — so normalise. Inert for a 6-digit TOTP.
        body: JSON.stringify({ token: this.code.replace(/\s/g, '').toLowerCase() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.usedBackup) {
          // Previously a blocking alert(). An inline message plus an immediate
          // redirect would render for zero frames, so hold briefly and give an
          // escape link rather than making people wait.
          this.notice = 'Backup code used. Generate fresh codes in Account settings.';
          this.loading = false;
          setTimeout(() => { window.location.href = this.returnPath(); }, 2500);
          return;
        }
        window.location.href = this.returnPath();
        return;
      }
      this.error = data.error || 'That code was not accepted.';
    } catch {
      this.error = "Couldn't reach Teknik. Check your connection and try again.";
    }
    this.loading = false;
  }

  static styles = [teknikTokens, teknikButtons, tkPrimitives, tkAuthCard, css``];

  render() {
    return html`
      <main>
        <div class="card">
          <div class="eyebrow">Two-factor</div>
          <h1 class="title">Confirm it's you</h1>
          <p class="sub">
            Enter the 6-digit code from your authenticator app. One of your 10-character
            backup codes works here too.
          </p>

          ${this.error ? html`<div class="tk-flash err" style="margin-bottom:1rem">${this.error}</div>` : ''}
          ${this.notice
            ? html`<div class="tk-flash ok" style="margin-bottom:1rem">
                ${this.notice}
                <a href="/" style="color:inherit;text-decoration:underline;margin-left:.4rem">Continue now →</a>
              </div>`
            : ''}

          <form @submit=${this.handleVerify}>
            <input
              class="tk-input code-input"
              type="text"
              inputmode="text"
              maxlength="10"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              placeholder="000000"
              autocomplete="one-time-code"
              autofocus
              .value=${this.code}
              @input=${(e: any) => (this.code = e.target.value)}
            />
            <button class="tk-btn primary full" type="submit" ?disabled=${this.loading || !this.code.trim()}>
              ${this.loading ? 'Verifying…' : 'Verify'}
            </button>
          </form>

          <div class="foot"><a href="/login">Cancel and sign in again</a></div>
        </div>
      </main>
    `;
  }
}
